import React from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { EmailNotification } from "@/api/fetchMyEmailNotifications/types";

/**
 * A full-screen modal to preview the HTML content of an EmailNotification.
 */
interface MyEmailNotificationPreviewProps {
  isOpen: boolean;
  notification: EmailNotification | null;
  onClose: () => void;
}

const MyEmailNotificationPreview: React.FC<MyEmailNotificationPreviewProps> = ({
  isOpen,
  notification,
  onClose,
}) => {
  if (!isOpen || !notification) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
      <div className="relative mx-auto flex min-h-full w-full max-w-4xl flex-col bg-white p-6">
        {/* Header */}
        <div className="flex items-center justify-between border-b pb-4">
          <h2 className="text-xl font-bold">
            Preview: {notification.subject || "Email"}
          </h2>
          <Button onClick={onClose} size="sm" variant="ghost">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Notification details */}
        <div className="flex items-center space-x-4 py-3 text-sm">
          <div>
            <span className="font-semibold mr-2">Status:</span>
            {notification.approved ? "Approved" : "Pending Approval"}
          </div>
          <div>
            <span className="font-semibold mr-2">Sent:</span>
            {notification.sent ? "Yes" : "No"}
          </div>
          {notification.chapterPermanentName && (
            <div>
              <span className="font-semibold mr-2">Chapter:</span>
              {notification.chapterPermanentName}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-grow overflow-auto py-4 px-2 border rounded">
          {notification.htmlContent ? (
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: notification.htmlContent }}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-gray-400">
              No HTML content available.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyEmailNotificationPreview;
